import React from 'react';
import { RiErrorWarningLine } from '@remixicon/react';
import { Button, Card, CardBody } from '@heroui/react';

interface Props {
  children: React.ReactNode;
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, () => this.setState({ hasError: false, error: null }));
      }

      return (
        <div className="flex min-h-screen items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardBody className="gap-4 p-6">
              <div className="flex flex-col items-center gap-3 text-center">
                <RiErrorWarningLine className="text-danger h-12 w-12" />
                <div>
                  <h2 className="text-lg font-semibold">Something went wrong</h2>
                  <p className="text-default-600 mt-1 text-sm">{this.state.error.message}</p>
                </div>
                <Button
                  color="primary"
                  onPress={() => {
                    this.setState({ hasError: false, error: null });
                    window.location.reload();
                  }}
                >
                  Reload Page
                </Button>
              </div>
              {process.env.NODE_ENV === 'development' && (
                <details className="mt-4">
                  <summary className="text-default-600 cursor-pointer text-xs">Stack Trace</summary>
                  <pre className="text-default-500 mt-2 max-h-48 overflow-auto rounded bg-gray-100 p-2 text-xs dark:bg-gray-800">
                    {this.state.error.stack}
                  </pre>
                </details>
              )}
            </CardBody>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
